import { Button, DropdownMenuItem, InfoCard, SplitButton, Textarea } from "@ondit/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAccess } from "~/core/access";
import { ApiError, api } from "~/core/api";
import type { DistributionPost, DistributionSource, PublishingPlatform } from "~/core/api/distribution";
import { useI18n, useT } from "~/core/i18n";
import { brandContracts } from "~/core/publishing";
import { keys } from "~/core/query";
import { Hint } from "~/core/ui/page";
import { Markdown } from "~/core/ui/markdown";
import { adaptArticle, distributionLength } from "~/modules/analytics/lib/distribution";
import { PlatformComposer } from "./composer";
import { ResultLink } from "./result-link";
export function PublishingContentEditor(props: {
	source: DistributionSource;
	platform: PublishingPlatform;
	entityId: string;
	existing?: DistributionPost;
	onStageChange?: (finishing: boolean) => void;
	taskId?: string;
	expandedPreview?: boolean;
	readOnly?: boolean;
	viewOnly?: boolean;
}) {
	const contract = brandContracts([props.platform]).byId(props.platform.id);
	return contract?.methods.some((method) => method === "browser" || method === "api") ? (
		<PlatformComposer
			key={`${props.source.id}:${props.source.revision}:${contract.id}`}
			source={props.source}
			onStageChange={props.onStageChange}
			platform={contract}
			entityId={props.entityId}
			taskId={props.taskId}
			expandedPreview={props.expandedPreview}
			readOnly={props.readOnly}
			viewOnly={props.viewOnly}
		/>
	) : (
		<ManualAdaptation {...props} />
	);
}

function ManualAdaptation({
	source,
	platform,
	entityId,
	existing,
	readOnly = false,
	viewOnly = false,
}: {
	source: DistributionSource;
	platform: PublishingPlatform;
	entityId: string;
	existing?: DistributionPost;
	readOnly?: boolean;
	viewOnly?: boolean;
}) {
	const t = useT();
	const { locale } = useI18n();
	const { editor: canEdit } = useAccess();
	const editor = canEdit && !readOnly;
	const recoveryKey = `ondit.distribution.${entityId}.${source.id}.${source.revision}.${platform.id}`;
	const [recovered] = useState(() => {
		try {
			return JSON.parse(sessionStorage.getItem(recoveryKey) ?? "null") as { body: string; url: string } | null;
		} catch {
			return null;
		}
	});
	const [body, setBody] = useState(recovered?.body ?? (existing?.body || adaptArticle(source, platform)));
	const [url, setUrl] = useState(recovered?.url ?? existing?.published_url ?? "");
	useEffect(() => {
		try {
			sessionStorage.setItem(recoveryKey, JSON.stringify({ body, url }));
		} catch {}
	}, [recoveryKey, body, url]);
	const [busy, setBusy] = useState(false);
	const queryClient = useQueryClient();
	const store = useMutation({
		mutationFn: (body: Parameters<typeof api.saveDistribution>[1]) => api.saveDistribution(entityId, body),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});
	const tooLong = platform.max_length !== null && distributionLength(body, platform) > platform.max_length;
	const stale = existing && existing.source_revision !== source.revision;
	async function save(resultUrl = url) {
		setBusy(true);
		try {
			await store.mutateAsync({
				page_id: source.id,
				platform_id: platform.id,
				source_revision: source.revision,
				body,
				published_url: resultUrl.trim() || null,
			});
			setUrl(resultUrl);
			toast.success(t("distribution.saved"));
			try {
				sessionStorage.removeItem(recoveryKey);
			} catch {}
		} finally {
			setBusy(false);
		}
	}
	async function copy(html = false) {
		try {
			if (html) {
				const { renderToStaticMarkup } = await import("react-dom/server");
				await navigator.clipboard.write([
					new ClipboardItem({
						"text/html": new Blob([renderToStaticMarkup(<Markdown text={body} />)], { type: "text/html" }),
						"text/plain": new Blob([body], { type: "text/plain" }),
					}),
				]);
			} else {
				await navigator.clipboard.writeText(body);
			}
			toast.success(t("badge.copied"));
		} catch {
			toast.error(t("distribution.copyFailed"));
		}
	}
	function download() {
		const blob = new Blob([body], { type: "text/markdown;charset=utf-8" });
		const href = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = href;
		a.download = `${source.id}-${platform.id}.md`;
		a.click();
		URL.revokeObjectURL(href);
	}
	if (viewOnly)
		return (
			<div className="platform-preview">
				<h3>{source.title}</h3>
				<Markdown text={body} />
			</div>
		);

	return (
		<div className="distribution-workspace">
			{/* One line of provenance rather than three: where this came from, which
			    revision, and what the platform allows are the same sentence. */}
			<p className="hint">
				<a href={source.canonical} target="_blank" rel="noopener noreferrer">
					{t("distribution.original")}
				</a>{" "}
				· {t("editor.revisionLabel")} {source.revision} ·{" "}
				<a href={platform.rules_url} target="_blank" rel="noopener noreferrer">
					{t("distribution.rules")}
				</a>{" "}
				· {t("distribution.links")}: {t(`distribution.${platform.links}`)}
			</p>
			{stale && <p className="banner banner-warn">{t("distribution.stale")}</p>}
			{!body.includes(source.canonical) && <p className="banner banner-warn">{t("distribution.missingSource")}</p>}
			<InfoCard className="analytics-form-panel">
				<div className="card-head">
					<div className="title-row">
						<h3 className="card-title">{t("distribution.preview")}</h3>
						<Hint text={t("distribution.previewHint")} />
					</div>
				</div>
				<fieldset disabled={!editor || busy} className="bare-fieldset">
					<Textarea
						aria-label={t("distribution.preview")}
						rows={10}
						value={body}
						onChange={(e) => setBody(e.target.value)}
					/>
				</fieldset>
				<div className="article-preview min-w-0 overflow-auto">
					<Markdown text={body} />
					<p className={tooLong ? "banner banner-warn" : "hint"}>
						{t("distribution.length", { n: distributionLength(body, platform) })}
						{platform.max_length ? ` / ${platform.max_length}` : ""} {tooLong ? t("distribution.tooLong") : ""}
					</p>
				</div>
			</InfoCard>
			<div className="table-tools">
				<div className="table-tools-filters">
					<SplitButton
						align="start"
						menuLabel={t("table.moreActions")}
						onClick={() => copy()}
						action={t("distribution.copy")}
					>
						<DropdownMenuItem onSelect={() => copy(true)}>{t("distribution.copyRich")}</DropdownMenuItem>
						<DropdownMenuItem onSelect={download}>{t("distribution.export")}</DropdownMenuItem>
						<DropdownMenuItem
							disabled={!editor || busy || !body.trim()}
							onSelect={() =>
								void save().catch((error) =>
									toast.error(error instanceof ApiError ? error.localized(locale) : String(error)),
								)
							}
						>
							{t("publishing.platform.save")}
						</DropdownMenuItem>
					</SplitButton>
				</div>
				<div className="table-tools-actions">
					<Button asChild variant="default">
						<a href={platform.url} target="_blank" rel="noopener noreferrer">
							{t("distribution.open")}
						</a>
					</Button>
					<ResultLink title={source.title} value={url} onSave={save} disabled={busy || !body.trim()} />
				</div>
			</div>
		</div>
	);
}
